import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import VendorCreationForm from "../../Vendor/VendorCreationForm";
import { updateRequestFormData } from "../../../../api/t2tRequestsApi";
import { useToast } from "../../Layout/T2TUIProvider";

const VENDOR_CODES = ["vendor_creation", "vendor_change", "vendor_extend", "vendor_creation_seasonal"];

const isVendorRequest = (code) =>
  typeof code === "string" && code.toLowerCase().includes("vendor");

export default function EditRequestModal({ request, onClose, onSaved }) {
  const toast   = useToast();
  const isVendor = isVendorRequest(request?.request_type_code);

  const [formData, setFormData] = useState(() => ({ ...(request?.form_data || {}) }));
  const [saving,   setSaving]   = useState(false);
  const [priority, setPriority] = useState(request?.priority || "medium");

  // VendorCreationForm calls onChange(key, value)
  const handleChange = useCallback((key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Simple editor for non-vendor requests
  const handleSimpleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const resp = await updateRequestFormData(request.request_id, { form_data: formData, priority });
    setSaving(false);
    if (resp.error) {
      toast(resp.error.message || "Failed to save.", "error");
      return;
    }
    toast("Request updated.", "success");
    onSaved();
  };

  const { form_data, request_type_fields, workflow_form_fields } = request || {};
  const fields = buildSimpleFields(form_data, request_type_fields, workflow_form_fields);

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ zIndex: 1060, background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white rounded shadow-lg d-flex flex-column"
        style={{ width: "min(860px, 96vw)", maxHeight: "92vh", overflow: "hidden" }}
      >
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
          <div>
            <span className="badge bg-light text-dark border font-monospace me-2">{request?.ref_id}</span>
            <span className="fw-semibold" style={{ fontSize: 15 }}>Edit Request</span>
          </div>
          <button className="btn-close" onClick={onClose} disabled={saving} />
        </div>

        {/* Priority row */}
        <div className="px-4 pt-3 pb-1 d-flex align-items-center gap-3 border-bottom">
          <label className="fw-semibold" style={{ fontSize: 13, minWidth: 60 }}>Priority</label>
          <select
            className="form-select form-select-sm"
            style={{ width: 140, fontSize: 13 }}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        {/* Form body */}
        <div className="overflow-auto px-4 py-3" style={{ flex: 1 }}>
          {isVendor ? (
            <VendorCreationForm
              formData={formData}
              onChange={handleChange}
              readOnly={false}
              accessMatrix={null}
              requestTypeCode={request?.request_type_code}
            />
          ) : (
            // Simple key-value grid for non-vendor requests
            <div className="row g-3">
              {fields.map((f) => (
                <div key={f.key} className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {f.label}
                  </label>
                  <input
                    className="form-control form-control-sm"
                    style={{ fontSize: 13 }}
                    value={formData[f.key] !== null && formData[f.key] !== undefined ? String(formData[f.key]) : ""}
                    onChange={(e) => handleSimpleChange(f.key, e.target.value)}
                  />
                </div>
              ))}
              {fields.length === 0 && (
                <div className="col-12 text-muted fst-italic" style={{ fontSize: 13 }}>
                  No editable fields found.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-top px-4 py-3 d-flex justify-content-end gap-2 bg-white">
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
            {saving
              ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
              : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

EditRequestModal.propTypes = {
  request: PropTypes.shape({
    request_id:         PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ref_id:             PropTypes.string,
    form_data:          PropTypes.object,
    request_type_code:  PropTypes.string,
    request_type_fields:  PropTypes.array,
    workflow_form_fields: PropTypes.array,
    priority:             PropTypes.string,
  }).isRequired,
  onClose:  PropTypes.func.isRequired,
  onSaved:  PropTypes.func.isRequired,
};

function buildSimpleFields(formData, requestTypeFields, workflowFormFields) {
  if (!formData || typeof formData !== "object") return [];

  // 1. Prefer request-type field definitions (snake_case keys, e.g. { field_key, label })
  if (Array.isArray(requestTypeFields) && requestTypeFields.length) {
    return requestTypeFields
      .filter((f) => formData[f.field_key] !== undefined)
      .map((f) => ({ key: f.field_key, label: f.label || f.field_key }));
  }

  // 2. Fall back to workflow form_fields — map BOTH field_id AND field_key
  if (Array.isArray(workflowFormFields) && workflowFormFields.length) {
    const labelMap = {};
    workflowFormFields.forEach((f) => {
      const label = f.label || (f.field_key ? f.field_key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : String(f.field_id || ""));
      if (f.field_id)  labelMap[String(f.field_id)]  = label;
      if (f.field_key) labelMap[String(f.field_key)] = label;
    });
    return Object.keys(formData).map((k) => ({
      key: k,
      label: labelMap[String(k)] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    }));
  }

  // 3. Last resort: humanise the raw key
  return Object.keys(formData).map((k) => ({
    key: k,
    label: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  }));
}
